import { useChat } from "@/features/chat"
import { ANALYTICS_EVENTS } from "@/features/analytics"
import {
  advisorsList,
  useAdvisorActions,
  AdvisorCarouselSection,
  CreditsPurchaseModal,
} from "@/features/advisors"
import { PersonalizedReports, ReportIds } from "@/features/today"

const EXCLUDE_PALMISTRY = [ReportIds.Palmistry] as const

import AnalyzeIcon from "@/assets/svg/palmistry/result/palmistry_micro.svg?react"

import { palmCopy } from "../config/copy"
import { palmLineSections } from "../config/line-sections"
import { palmSlides } from "../config/slides"
import { formatReportDate } from "../lib/format-report-date"
import { getAstrology, getLineText, getThingsText, parseZodiacSide } from "../lib/palm-reading"
import type { PalmReading, PalmReadingContent } from "../types"
import { PalmHero } from "./palm-hero"
import { PalmLineSection } from "./palm-line-section"

const continueJourneyAdvisors = [
  advisorsList[0],
  advisorsList[12],
  advisorsList[7],
  advisorsList[1],
  advisorsList[8],
]

interface PalmResultProps {
  content: PalmReadingContent
  reading: PalmReading
  name: string
  zodiac: string
}

/** Full palm reading report. Ported from ResultPalmReadingV4 (V4). */
export function PalmResult({ content, reading, name, zodiac }: PalmResultProps) {
  const { userMainSubscriptionId } = useChat()
  const { isPaywallOpen, closePaywall, handleChatWithAdvisor, handleAdvisorInner } =
    useAdvisorActions()

  const astrology = getAstrology(content, zodiac)

  return (
    <div className="bg-dark-base pb-10 text-purple-light">
      <PalmHero reading={reading} />

      <div className="px-4">
        <div className="my-10 h-px w-full bg-purple-light/10" />
        <h3 className="font-display text-2xl font-medium text-purple-light">
          {palmCopy.heartOfNature.title}
        </h3>
        <p className="mb-6 mt-2 font-body text-base text-purple-light/70">
          {palmCopy.heartOfNature.subtitle}
        </p>
      </div>

      {/* "What your hand reveals" slides */}
      <div className="-mr-4 flex gap-4 overflow-x-auto pl-4 pr-4 [scrollbar-width:none]">
        {palmSlides.map((slide) => {
          const labels =
            slide.source === "strong"
              ? parseZodiacSide(astrology.strongSides)
              : slide.source === "weak"
                ? parseZodiacSide(astrology.weakSides)
                : (slide.labels ?? [])
          const { Icon } = slide
          return (
            <div
              key={slide.title}
              className="w-[320px] shrink-0 rounded-3xl bg-gradient-to-bl from-purple-light/10 to-purple-light/5 p-4 pb-6"
            >
              <span className="[&_svg]:size-14">
                <Icon />
              </span>
              <h4 className="my-4 font-display text-xl font-medium text-purple-light">
                {slide.title}
              </h4>
              <div className="flex flex-wrap gap-[5px]">
                {labels.map((label) => (
                  <span
                    key={label}
                    className="rounded-[23px] border border-purple-light/20 px-3 py-1.5 font-body text-sm text-purple-light/70"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {palmLineSections.map((config) => (
        <PalmLineSection
          key={config.lineKey}
          config={config}
          description={getLineText(content, reading, config.lineKey, name)}
          summaryDescription={getThingsText(content, reading, config.thingsKey, name)}
          percent={reading[config.thingsKey].percent}
          onAsk={() => handleChatWithAdvisor(advisorsList[0])}
        />
      ))}

      <div className="mt-10 px-4">
        <h3 className="font-display text-[40px] font-medium text-purple-light">
          {palmCopy.conclusion.title}
        </h3>
        <p className="mt-4 font-body text-base text-purple-light/70">{astrology.moreDetails}</p>
      </div>

      <div className="mt-10 px-4">
        <h3 className="font-display text-2xl font-medium text-purple-light">
          {palmCopy.scanBasedReading.title}
        </h3>
        <div className="mt-3 flex items-center gap-4">
          <span className="size-14 shrink-0 [&_svg]:size-14">
            <AnalyzeIcon />
          </span>
          <p className="font-body text-base text-purple-light/70">
            {palmCopy.scanBasedReading.description(formatReportDate(reading.lastUpdated, "long"))}
          </p>
        </div>
      </div>

      <PersonalizedReports excludeIds={EXCLUDE_PALMISTRY} />

      <div className="mt-10">
        <AdvisorCarouselSection
          title={palmCopy.continueJourney.title}
          subtitle={palmCopy.continueJourney.description}
          advisors={continueJourneyAdvisors}
          chatEvent={ANALYTICS_EVENTS.CHAT_WITH_RECOMMENDED_ADVISOR}
          onCardClick={handleAdvisorInner}
          onChatClick={handleChatWithAdvisor}
        />
      </div>

      <CreditsPurchaseModal
        isOpen={isPaywallOpen}
        onClose={closePaywall}
        userMainSubscriptionId={userMainSubscriptionId}
        variant="main-page"
      />
    </div>
  )
}
